import { createHttpDeviceApi } from './api/device-api.js';
import { createBlocklist } from './blocklist.js';
import { createDownloader } from './cache/downloader.js';
import { createIdbAssetStore } from './cache/idb-store.js';
import { createCacheManager } from './cache/manager.js';
import { createMemoryAssetStore, type AssetStore } from './cache/store.js';
import { createTimeSource } from './clock.js';
import type { PlayerConfig } from './config.js';
import { BOOTSTRAP_EVENT_BATCH_SIZE } from './constants.js';
import { createEventQueue, createMemoryEventQueueStore, type EventQueueStore } from './events/queue.js';
import { createIdbEventQueueStore } from './events/idb-store.js';
import { createEventUploader } from './events/uploader.js';
import { parseFleetBlocklistPayload } from './fleet-blocklist.js';
import { sha256Hex } from './hash/sha256.js';
import { createHeartbeatEmitter } from './heartbeat.js';
import { createBuiltInHouseContent, createCachedHouseContent } from './house.js';
import { createUuidV7Factory } from './ids.js';
import { consoleSink, createLogger } from './log.js';
import { attachManifestNudge } from './manifest-nudge.js';
import { createDomRenderer } from './playback/dom-renderer.js';
import { createPlayer, type Player } from './player.js';
import {
  createIdbPlayerStateStore,
  createMemoryPlayerStateStore,
  type PlayerStateStore,
} from './state/persist.js';
import {
  createPairingCodeReader,
  createPairingQrReader,
  createTokenReader,
  detectShellBridge,
} from './shell.js';
import { openPlayerDatabase } from './storage/idb.js';
import { systemTimer, type Timer } from './timer.js';
import { createDocumentVisibility } from './visibility.js';

/**
 * Wires the player for a browser or WebView. Every dependency is injected, so the
 * headless harness builds the same state machine with fakes.
 *
 * If IndexedDB is unavailable the player degrades to in-memory storage rather than
 * refusing to play: a screen with no persistence is worse than a screen with no
 * screen, but only slightly, and it still shows an ad (invariant 9).
 */

export interface BrowserPlayerOptions {
  readonly config: PlayerConfig;
  readonly root: HTMLElement;
  readonly document: Document;
  readonly window: Window & typeof globalThis;
  readonly timer?: Timer;
}

export interface BrowserPlayer {
  readonly player: Player;
  readonly persistent: boolean;
}

export async function createBrowserPlayer(options: BrowserPlayerOptions): Promise<BrowserPlayer> {
  const timer = options.timer ?? systemTimer;
  const logger = createLogger({ now: () => Date.now(), sink: consoleSink });

  let assetStore: AssetStore = createMemoryAssetStore();
  let queueStore: EventQueueStore = createMemoryEventQueueStore();
  let stateStore: PlayerStateStore = createMemoryPlayerStateStore();
  let persistent = false;

  try {
    const db = await openPlayerDatabase(options.window.indexedDB);
    assetStore = createIdbAssetStore(db);
    queueStore = createIdbEventQueueStore(db);
    stateStore = createIdbPlayerStateStore(db);
    persistent = true;
  } catch (error) {
    logger.error('storage.indexeddb_unavailable', {
      reason: error instanceof Error ? error.message : String(error),
    });
  }

  const restored = await stateStore.load().catch(() => null);
  const time = createTimeSource({
    deviceClock: () => Date.now(),
    ...(restored === null ? {} : { initialOffsetMs: restored.clock_offset_ms }),
  });

  const bridge = detectShellBridge(options.window);
  const deviceToken = createTokenReader({ bridge, search: options.window.location.search });
  const pairingCode = createPairingCodeReader({ bridge, search: options.window.location.search });
  const pairingQr = createPairingQrReader({ bridge });

  const api = createHttpDeviceApi({
    config: options.config,
    fetch: (url, init) => options.window.fetch(url, init),
    deviceToken,
    logger,
    observeServerTime: (serverMs) => {
      time.observeServerTime(serverMs);
    },
  });

  const downloader = createDownloader({
    fetch: (url, init) => options.window.fetch(url, init),
    sleep: (ms) => timer.sleep(ms),
    logger,
    onPartial: async (bytes) => {
      // Keyed by URL-independent digest is impossible mid-download; the manager
      // clears partials once the asset verifies.
      logger.debug('asset.partial_retained', { byte_length: bytes.byteLength });
    },
  });

  const cache = createCacheManager({
    store: assetStore,
    downloader,
    hasher: sha256Hex,
    logger,
    now: () => Date.now(),
    ceilingBytes: options.config.cacheMaxBytesBootstrap,
  });

  const newId = createUuidV7Factory({ now: () => time.nowMs() });

  const queue = createEventQueue({
    store: queueStore,
    logger,
    newId,
    deviceNow: () => time.deviceNowMs(),
    clockUnsynced: () => time.isUnsynced(),
  });

  const renderer = createDomRenderer({
    root: options.root,
    document: options.document,
    timer,
    logger,
  });

  // The uploader's batch size and the heartbeat's manifest version both come from
  // the manifest the player is running, which does not exist until it is built.
  let playerRef: Player | null = null;

  const uploader = createEventUploader({
    queue,
    api,
    time,
    batchSize: () => playerRef?.eventBatchSize() ?? BOOTSTRAP_EVENT_BATCH_SIZE,
    logger,
  });

  const house =
    options.config.houseContentUrl === null
      ? createBuiltInHouseContent()
      : createCachedHouseContent({
          logger,
          load: async () => {
            const url = options.config.houseContentUrl;
            if (url === null) return null;
            const result = await downloader.download(url, { resumeFrom: null });
            if (!result.ok) return null;
            const digest = await sha256Hex(result.bytes);
            return {
              sha256: digest,
              bytes: result.bytes,
              byte_length: result.bytes.byteLength,
              media_type: guessMediaType(url),
              stored_at_ms: Date.now(),
              last_access_ms: Date.now(),
            };
          },
        });

  // What keeps the shell's watchdog from restarting the WebView underneath a healthy loop.
  // Omitted entirely rather than passed as a no-op when there is no shell (a browser tab has
  // no watchdog) or when the shell predates the shim, so the player skips the timer instead
  // of ticking against nothing.
  const aliveReport =
    bridge.reportAlive === undefined
      ? {}
      : {
          reportAlive: (): void => {
            bridge.reportAlive?.();
          },
        };

  // D254: what lets a revoked TV ask for a new code. Omitted on a shell that predates it, which
  // leaves the not-activated card; a browser tab has no token to forget.
  const forget =
    bridge.clearDeviceCredentials === undefined
      ? {}
      : {
          forgetDevice: (): void => {
            bridge.clearDeviceCredentials?.();
          },
        };

  const player = createPlayer({
    api,
    cache,
    renderer,
    queue,
    uploader,
    heartbeat: createHeartbeatEmitter({
      api,
      time,
      logger,
      appVersion: bridge.getAppVersion?.() ?? options.config.appVersion,
      bootedAtDeviceMs: time.deviceNowMs(),
      cacheBytes: () => cache.bytesUsed(),
      freeBytes: () => bridge.getFreeBytes?.() ?? null,
      manifestVersion: () => playerRef?.activeManifestVersion() ?? null,
      lastError: () => logger.lastError()?.event ?? null,
      queuedEvents: () => queue.size(),
      wifiSsid: () => bridge.getWifiSsid?.() ?? null,
      foreground: () => bridge.getForeground?.() ?? null,
      hardwareModel: () => bridge.getHardwareModel?.() ?? null,
      osVersion: () => bridge.getOsVersion?.() ?? null,
      sdkInt: () => bridge.getSdkInt?.() ?? null,
      overlayOpGranted: () => bridge.getOverlayOpGranted?.() ?? null,
      networkType: () => bridge.getNetworkType?.() ?? null,
      screenOn: () => bridge.getScreenOn?.() ?? null,
      signingSha256: () => bridge.getSigningSha256?.() ?? null,
    }),
    blocklist: createBlocklist(logger),
    house,
    stateStore,
    time,
    timer,
    logger,
    newId,
    deviceToken,
    pairingCode,
    pairingQr,
    ...aliveReport,
    ...forget,
    // dwf-063: the web half of the pair. `MainActivity.onPause()` pauses the WebView on
    // Fire OS; this is what covers a browser tab for QA and any Android TV stick where
    // there is no shell to do it.
    visibility: createDocumentVisibility(options.document),
  });
  playerRef = player;

  // The MQTT nudge (D37) is what makes an approved ad reach a screen in seconds
  // rather than at the next poll; without a shell there is no channel and the poll
  // is the whole delivery path. A nudge landing in the gap before `start()` restores
  // the cached manifest is discarded with the rest of that state and refetched by the
  // boot sync, so the worst case is one wasted request, never a missed version.
  attachManifestNudge({ bridge, player, logger });

  bridge.onFleetBlocklist?.((payload) => {
    const entries = parseFleetBlocklistPayload(payload);
    if (entries === null) {
      logger.warn('blocklist.fleet_payload_invalid', {});
      return;
    }
    player.applyFleetBlocklist(entries);
  });

  return { player, persistent };
}

function guessMediaType(url: string): string {
  if (/\.mp4(\?|$)/i.test(url)) return 'video/mp4';
  if (/\.webm(\?|$)/i.test(url)) return 'video/webm';
  if (/\.png(\?|$)/i.test(url)) return 'image/png';
  if (/\.svg(\?|$)/i.test(url)) return 'image/svg+xml';
  return 'image/jpeg';
}
